// START IMPORTS
import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  PaymentsSelectionModal,
  PayPalRedirectionModal,
  RadioPaymentsModal,
  ViewOrderHistory,
} from '@ltvco/refresh-lib/payments';
import { Grid, Box } from '@ltvco/refresh-lib/theme';
import { capitalizeEachWord, DateUtil } from '@ltvco/refresh-lib/utils';
import {
  AccountInformation,
  GenericCtaCard,
  LimitedPlanProgress,
  LimitedPlanProgressVIN,
  MyAccountHeader,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  AccountEditModals,
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import {
  AccountInformationOptions,
  GenericCtaCardProps,
  editOptionClicked,
} from './BillingConstants';
// END IMPORTS

interface BillingProps {
  onCancelConfirmCancelModal?: () => void;
  optoutCtaCards?: boolean;
  link?: string;
}
export function Billing({
  onCancelConfirmCancelModal,
  optoutCtaCards = false,
  link,
}: BillingProps) {
  // START HOOKS_AND_QUERIES
  const navigate = useNavigate();
  const {
    session: { account },
  } = useSession();
  const {
    isLimitedUser,
    isUnlimitedUser,
    reportLimit,
    reportsRun,
    reportsRunPercentage,
    isLoading,
    nextRenewalDate,
  } = useLimitedPlanInfo();
  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();
  const {
    isInInternationalOrVinVariations,
    vinVariation,
    internationalLanguageVariation,
    isBlocked,
    isHidden,
  } = useSubscriptionAccessConfigContext();
  const isInUpdatePaymentMethodVariation = useFeatureIsOn(
    'update-payment-method'
  );
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [openPaymentsSelectionModal, setOpenPaymentsSelectionModal] =
    useState<boolean>(false);
  const [openPaymentsUpdateModal, setOpenPaymentsUpdateModal] =
    useState<boolean>(false);
  const [openPaypalRedirection, setOpenPaypalRedirection] =
    useState<boolean>(false);
  const [paypalState, setPaypalState] = useState<'success' | 'canceled'>(
    'success'
  );
  const [editing, setEditing] = useState<string | null>('');
  const [searchType, setSearchType] = useState('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const dateUtil = new DateUtil();

  const onVariationCtaClick = (searchType: string) => {
    setSearchType(
      capitalizeEachWord(
        searchType === 'background report' ? 'person' : searchType
      )
    );
    openFeatureBlockedModal();
  };

  const checkIfFeatureIsBlocked = (searchType: string) => {
    if (searchType === 'people') {
      return isBlocked('person');
    }
    return isBlocked(searchType);
  };

  const { ctaCardProps, length } = GenericCtaCardProps(
    navigate,
    onVariationCtaClick,
    checkIfFeatureIsBlocked,
    isHidden,
    isInUpdatePaymentMethodVariation,
    () => togglePaymentsSelectionModal()
  );

  const planUniqueName =
    account?.account?.subscription_info?.subscription_plan_unique_name ?? '';
  const isInternationalSpanishPlan =
    constants?.internationalSpanishPlanNames?.includes(planUniqueName) ?? false;
  const isInternationalFraudscanOnlyPlan =
    constants?.internationalFraudscanOnlyPlanNames?.includes(planUniqueName) ??
    false;
  const showRenewalVariant =
    vinVariation ||
    isInternationalSpanishPlan ||
    isInternationalFraudscanOnlyPlan;

  const CancelMyAccount = {
    name: 'Cancel My Account',
    display: 'Cancel My Account',
    value: 'Cancel My Account',
    constantValue: 'CANCEL_MY_ACCOUNT',
  };
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    document.title = 'My Account - BeenVerified';
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bvppcanc')) {
      setPaypalState('canceled');
      setOpenPaypalRedirection(true);
      return;
    }
    if (urlParams.get('bvpp') && urlParams.get('token')) {
      setPaypalState('success');
      setOpenPaypalRedirection(true);
    }
    const exist = AccountInformationOptions.find(
      (option) => option.value === 'Cancel My Account'
    );
    if (!exist) {
      AccountInformationOptions.push(CancelMyAccount);
    }
  }, []);
  // END EFFECTS

  // START EVENT_HANDLERS
  const togglePaymentsSelectionModal = () =>
    setOpenPaymentsSelectionModal(!openPaymentsSelectionModal);

  const togglePaymentsUpdateModal = () => {
    setOpenPaymentsUpdateModal(!openPaymentsUpdateModal);
    if (!openPaymentsSelectionModal) {
      togglePaymentsSelectionModal();
    }
  };

  const togglePaypalRedirectionModal = () =>
    setOpenPaypalRedirection(!openPaypalRedirection);

  const finishEditing = () => {
    setEditing('');
  };

  function handleAddPaymentMethod() {
    setOpenPaymentsSelectionModal(false);
    setOpenPaymentsUpdateModal(true);
  }
  // END EVENT_HANDLERS

  // START DATA_PREPARATION
  const has_sso_connections = account?.account?.user_info?.has_sso_connections;

  const updatedItems = AccountInformationOptions.filter((item) => {
    if (has_sso_connections && item.constantValue === 'CHANGE_PASSWORD') {
      return false;
    }
    if (
      isInUpdatePaymentMethodVariation &&
      item.constantValue === 'EDIT_PAYMENT_INFO'
    ) {
      return false;
    }
    return true;
  });

  if (has_sso_connections) {
    updatedItems.push({
      name: 'Send Password Reset Email',
      display: 'Send Password Reset Email',
      value: 'Send Password Reset Email',
      constantValue: 'PASSWORD_RESET_EMAIL',
    });
  }
  // END DATA_PREPARATION

  // START RENDER
  return (
    <>
      <MyAccountHeader isUnlimited={isUnlimitedUser} />
      <>
        {isLimitedUser &&
          !isLoading &&
          !internationalLanguageVariation &&
          (showRenewalVariant ? (
            <LimitedPlanProgressVIN
              progressValue={reportsRunPercentage}
              reportsLimit={reportLimit}
              reportsRun={reportsRun}
              nextRenewalDate={
                dateUtil?.parseDateFromString(
                  nextRenewalDate,
                  'MMMM dd, yyyy'
                ) || ''
              }
            />
          ) : (
            <LimitedPlanProgress
              plansWithout5ReportBlocker={
                constants?.data?.plansWithout5ReportBlocker
              }
              validReportCountThreshold={
                constants?.data?.validReportCountThreshold
              }
              progressValue={reportsRunPercentage}
              reportsLimit={reportLimit}
              reportsRun={reportsRun}
              link={link || '/upgrade/plan'}
              blockUpgradeWindowThreshold={
                constants?.data?.blockUpgradeWindowThreshold
              }
            />
          ))}

        {isInInternationalOrVinVariations || optoutCtaCards ? null : (
          <Grid container spacing={2} justifyContent="center">
            {[...Array(length)].map((_x, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <GenericCtaCard {...ctaCardProps[index]} />
              </Grid>
            ))}
          </Grid>
        )}

        <FeatureBlockedModal
          isOpen={isFeatureBlockedModalOpen}
          onClose={closeFeatureBlockedModal}
          searchType={searchType}
          paymentShowModalNumber={paymentShowModalNumber}
          setPaymentShowModalNumber={setPaymentShowModalNumber}
        />

        <AccountEditModals
          editing={editing}
          finishEditing={finishEditing}
          onCancelConfirmCancelModal={onCancelConfirmCancelModal}
        />

        <Box
          mt={2.5}
          nonce={undefined}
          onResize={undefined}
          onResizeCapture={undefined}
        >
          <AccountInformation
            editOptions={updatedItems}
            editOptionClick={(value: string | null) => {
              editOptionClicked(
                value,
                setOpenPaymentsSelectionModal,
                setEditing,
                updatedItems
              );
            }}
          />
        </Box>
        <ViewOrderHistory />
      </>
      <PaymentsSelectionModal
        open={openPaymentsSelectionModal}
        onClose={togglePaymentsSelectionModal}
        onAddPaymentMethod={handleAddPaymentMethod}
      />
      <RadioPaymentsModal
        isOpen={openPaymentsUpdateModal}
        onCloseHandle={togglePaymentsUpdateModal}
      />

      <PayPalRedirectionModal
        paypalState={paypalState}
        isOpen={openPaypalRedirection}
        onCloseHandle={togglePaypalRedirectionModal}
      />
    </>
  );
  // END RENDER
}
